import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DashboardLayout from "../layouts/dashboard";
import { useNavigate, useParams } from "react-router";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import { useExpectedDeliveryDetail } from "../../api/expecteddeliveries";
import { WeightDisplay } from "../../interface/shared/weightdisplay";

export default function ExpectedDeliveryDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { delivery, errors, loading, fetchDelivery } = useExpectedDeliveryDetail(id);

    return (
        <DashboardLayout>
            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/goods-in")}>
                        Back
                    </Button>
                </Stack>

                {errors.length > 0 && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchDelivery}>
                                Retry
                            </Button>
                        }
                    >
                        {errors.join(" ")}
                    </Alert>
                )}

                {loading ? (
                    <_DetailsSkeleton />
                ) : delivery ? (
                    <StaggerContainer>
                        <StaggerItem>
                            <_ExpectedDeliverySummary
                                poNumber={delivery.po_number}
                                supplierName={delivery.supplier?.name ?? "-"}
                                deliveryDate={delivery.delivery_date}
                                placedAt={delivery.placed_at}
                                amendedAt={delivery.amended_at}
                                completedAt={delivery.completed_at}
                                lineCount={delivery.lines.length}
                                palletCount={delivery.pallet_qty}
                            />
                        </StaggerItem>

                        <StaggerItem>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                Lines ({delivery.lines.length})
                            </Typography>
                            <Card variant="outlined">
                                <CardContent>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Line</TableCell>
                                                <TableCell>Code</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Qty / Weight</TableCell>
                                                <TableCell align="right">Pack Qty</TableCell>
                                                <TableCell align="right">Pallet Qty</TableCell>
                                                <TableCell>Units</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {delivery.lines.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                                                            No lines available.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                delivery.lines.map((line) => (
                                                    <TableRow key={`${line.line_no}-${line.supplier_code}`}>
                                                        <TableCell>{line.line_no}</TableCell>
                                                        <TableCell>{line.product?.code ?? "-"}</TableCell>
                                                        <TableCell>{line.description}</TableCell>
                                                        <TableCell align="right">{line.weight ? <WeightDisplay value={line.weight} /> : line.qty}</TableCell>
                                                        <TableCell align="right">{line.pack_qty}</TableCell>
                                                        <TableCell align="right">{line.pallet_qty}</TableCell>
                                                        <TableCell>{line.units}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </StaggerItem>
                    </StaggerContainer>
                ) : null}
            </Box>
        </DashboardLayout>
    );
}

function _ExpectedDeliverySummary({
    poNumber,
    supplierName,
    deliveryDate,
    placedAt,
    amendedAt,
    completedAt,
    lineCount,
    palletCount,
}: {
    poNumber: string;
    supplierName: string;
    deliveryDate: Date | null;
    placedAt: Date | null;
    amendedAt: Date | null;
    completedAt: Date | null;
    lineCount: number;
    palletCount: number;
}) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">{poNumber || "-"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {supplierName}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Chip label={`${lineCount} lines`} size="small" />
                        <Chip label={`${palletCount} pallets`} size="small" />
                        <Chip
                            label={completedAt ? "Completed" : "Pending"}
                            size="small"
                            color={completedAt ? "success" : "warning"}
                        />
                    </Stack>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Delivery date</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {deliveryDate ? deliveryDate.toLocaleDateString() : "-"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Placed</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {placedAt ? placedAt.toLocaleDateString() : "-"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Amended</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {amendedAt ? amendedAt.toLocaleDateString() : "-"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {completedAt ? completedAt.toLocaleDateString() : "-"}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={220} height={32} />
                    <Skeleton variant="text" width={180} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 3 }}>
                                <Skeleton variant="text" width={90} height={16} />
                                <Skeleton variant="text" width={120} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={140} height={28} sx={{ mt: 4, mb: 2 }} />
            <Card variant="outlined">
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Line</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Qty / Weight</TableCell>
                                <TableCell align="right">Pack Qty</TableCell>
                                <TableCell align="right">Pallet Qty</TableCell>
                                <TableCell>Units</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </>
    );
}
